import {Box} from 'src/design/atoms/Box';
import {useLocalStorage} from '@mantine/hooks';
import type {
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from 'mantine-react-table';
import React, {useCallback} from 'react';
import {useSelector} from 'react-redux';
import type {
  TaskDefinitionSimpleResponse,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {TaskDefinitionStateEnum} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {PersonSelectBox} from 'src/app/databound-elements/PersonSelectBox';
import {Checkbox} from 'src/design/atoms/Checkbox';
import {useTaskDefinitionColumns} from 'src/app/tasks/TaskDefinitionTable/hooks/useTaskDefinitionColumns';
import {useGetTaskDefinitionListV2Query} from 'src/redux/queries/taskDefinition';
import type {RootState} from 'src/redux/store';
import type {ISelectOption} from 'src/types/Form';

export interface TaskDefinitionTableProps {
  identifier: string;
  taskType?: TaskTypeEnum;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;

  filterAssetId?: number;
  filterFormId?: number;

  onCreate?: () => void;
  onViewRowClick: (row: TaskDefinitionSimpleResponse) => void;
}

export const TaskDefinitionTable: React.FC<TaskDefinitionTableProps> = ({
  identifier,
  taskType,
  defaultSorting,
  defaultColumnFilters,
  onCreate,
  onViewRowClick,
  filterAssetId,
  filterFormId,
}) => {
  const {session} = useSelector((state: RootState) => state.session);
  const user = session?.user;
  const [createdByMe, setCreatedByMe] = useLocalStorage({
    key: `${identifier}-filter-created-by-me`,
    defaultValue: false,
  });
  const [assignedToUserId, setAssignedToUserId] = useLocalStorage<
    number | undefined
  >({
    key: `${identifier}-filter-assigned-to`,
    defaultValue: undefined,
  });

  const columns = useTaskDefinitionColumns();

  const handleCreatedByMeFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {checked} = e.target;
    setCreatedByMe(checked);
  };

  const handleAssignedToChange = useCallback(
    (value: ISelectOption | null) => {
      if (value) {
        setAssignedToUserId(value.id as number);
      } else {
        setAssignedToUserId(undefined);
      }
    },
    [setAssignedToUserId]
  );

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: defaultSorting ?? [{id: 'createdAt', desc: true}],
    defaultShowColumnFilters: true,
    defaultColumnFilters: defaultColumnFilters ?? [
      {
        id: 'state',
        value: [
          TaskDefinitionStateEnum.Draft.toString(),
          TaskDefinitionStateEnum.Published.toString(),
        ],
      },
    ],
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  // Ensure assignedToUserId is a valid number, not a stringified "undefined"
  const validAssignedToUserId =
    typeof assignedToUserId === 'number' ? assignedToUserId : undefined;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetTaskDefinitionListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
        createdByUserId: createdByMe ? user?.id : undefined,
        assignedToUserId: validAssignedToUserId,
        taskType: taskType,
        filterAssetId: filterAssetId,
        filterFormId: filterFormId,
        includeDemonstrationGenerated: false,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => onViewRowClick(row.original)} />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <Checkbox
              checked={createdByMe}
              onChange={handleCreatedByMeFilter}
              label="Created By Me"
            />
            <Box w={200}>
              <PersonSelectBox
                label=""
                placeholder="Search Assigned To"
                value={assignedToUserId?.toString()}
                onSelectChangeCallback={handleAssignedToChange}
              />
            </Box>
          </TableToolbar.Filters>
          {onCreate && (
            <TableToolbar.Actions>
              <TableToolbar.Action variant="create" onClick={onCreate} />
            </TableToolbar.Actions>
          )}
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
